import {Icon} from '@/app/components/Icon';
import {cn} from '@/lib/utils';
import {useContext, useState} from 'react';
import {confirm} from '@/components/app/confirmation';
import {Modal} from '@/components/app/modal';
import {useAppDispatch} from '../../../app/hooks/useAppDispatch';
import {useGetBiotwinQuery, useResetBiotwinMutation} from '../../../app/services/biotwinApi';
import {BiotwinSettingsForm} from '../../biotwin-settings/components/BiotwinSettingsForm';
import {MenuButton} from '../../../app/components/MenuButton';
import {toastError} from '../../../app/toast';
import {blockUI, unblockUI} from '../../core/coreSlice';
import {useBlockUI} from '../../core/hooks/useBlockUI';
import {BiotwinShareForm} from '../../biotwin-share/components/BiotwinShareForm';
import {useSession} from '../../auth/hooks/useSession';
import {useDemo} from '../../demo/hooks/useDemo';
import {PersonContext} from '../../person/contexts/PersonContext';
import {useExportBiotwin} from '../hooks/useExportBiotwin';
import {BiotwinScore} from '../types';
import {BiotwinHistory} from './BiotwinHistory';
import {BiotwinInfo} from './BiotwinInfo';
import {ManageDemo} from './ManageDemo';
import {ManageSample} from './ManageSample';
import {ScoreInfo} from './ScoreInfo';
export interface BiotwinActionsProps {
  className?: string;
}
export const BiotwinActions = ({className}: BiotwinActionsProps) => {
  const {userId} = useContext(PersonContext);
  const dispatch = useAppDispatch();
  const session = useSession();
  const {data: biotwin} = useGetBiotwinQuery({
    id: userId,
  });
  const [settingsDialogVisible, setSettingsDialogVisible] = useState(false);
  const [scoreDialogState, setScoreDialogState] = useState<
    | {
        visible: false;
      }
    | {
        biotwinScore: BiotwinScore;
        visible: true;
      }
  >({
    visible: false,
  });
  const [demoDialogVisible, setDemoDialogVisible] = useState(false);
  const [shareDialogVisible, setShareDialogVisible] = useState(false);
  const [sampleDialogVisible, setSampleDialogVisible] = useState(false);
  const {isDemo, showLimitReachedDialog} = useDemo();
  const [historyDialogVisible, setHistoryDialogVisible] = useState(false);
  const [infoDialogVisible, setInfoDialogVisible] = useState(false);
  const [resetBiotwin, {isLoading: isLoadingResetBiotwin}] = useResetBiotwinMutation();
  const {exportBiotwin} = useExportBiotwin();
  useBlockUI(isLoadingResetBiotwin);
  return (
    <>
      <MenuButton
        className={cn(className, 'rounded-full')}
        data-name="user-biotwin-actions-button"
        actions={[
          {
            onSelect: () => {
              if (isDemo) {
                showLimitReachedDialog({
                  message: 'Create an account to share biotwins with others.',
                  warning: 'Sharing is not available in demo.',
                });
              } else {
                setShareDialogVisible(true);
              }
            },
            icon: 'share-alt',
            label: 'Share biotwin',
          },
          {
            onSelect: () => setHistoryDialogVisible(true),
            icon: 'history',
            label: 'Show timeline',
            visible: !biotwin?.isDemo && !biotwin?.isSample,
          },
          {
            onSelect: () => setInfoDialogVisible(true),
            icon: 'info-circle',
            label: 'Show info',
            visible: !biotwin?.isDemo && !biotwin?.isSample,
          },
          {
            onSelect: () => setDemoDialogVisible(true),
            icon: 'cog',
            label: 'Show demo settings (Admin)',
            visible: session?.isAdmin === 'true',
          },
          {
            onSelect: () => setSampleDialogVisible(true),
            icon: 'cog',
            label: 'Show sample settings (Admin)',
            visible: session?.isAdmin === 'true',
          },
          {
            onSelect: () => setSettingsDialogVisible(true),
            icon: 'cog',
            label: 'Show biobook settings',
          },
          {
            onSelect: async () => {
              try {
                dispatch(blockUI());
                await exportBiotwin(biotwin!.id);
              } catch (e) {
                toastError('Failed to export biotwin');
              } finally {
                dispatch(unblockUI());
              }
            },
            icon: 'file-export',
            label: 'Export Biotwin (Admin)',
            visible: session?.isAdmin === 'true',
          },
          {
            onSelect: () => onRemoveAllConditions(),
            icon: 'trash',
            label: 'Remove All Features (Admin)',
            visible: session?.isAdmin === 'true',
          },
        ]}
        align={'end'}
        variant="text"
        type="button"
        startIcon={<Icon icon={'ellipsis-h'} />}
        aria-label={'More actions'}
        size="icon"
      />

      <Modal title="Biobook Settings" onClose={() => setSettingsDialogVisible(false)} open={settingsDialogVisible}>
        <BiotwinSettingsForm userId={userId} />
      </Modal>

      <Modal
        title={scoreDialogState.visible ? scoreDialogState.biotwinScore.scoreShortDescription : 'Score'}
        onClose={() =>
          setScoreDialogState({
            visible: false,
          })
        }
        open={scoreDialogState.visible}
      >
        {scoreDialogState.visible && <ScoreInfo biotwinScore={scoreDialogState.biotwinScore} className="w-[30rem]" />}
      </Modal>

      <Modal
        className={'w-[30rem]'}
        title={`Share ${biotwin?.name ?? 'Biotwin'}`}
        onClose={() => setShareDialogVisible(false)}
        open={shareDialogVisible}
      >
        {biotwin != null && (
          <BiotwinShareForm biotwinId={biotwin.id} onSubmitSuccess={() => setShareDialogVisible(false)} />
        )}
      </Modal>

      <Modal
        className={'h-full'}
        title={`${biotwin?.name}'s Timeline`}
        onClose={() => setHistoryDialogVisible(false)}
        style={{
          maxWidth: '60rem',
          width: 'calc(100% - 3rem)',
        }}
        open={historyDialogVisible}
      >
        {biotwin && <BiotwinHistory userId={biotwin.id} />}
      </Modal>

      <Modal
        className={'w-[30rem]'}
        title={'Biotwin Info'}
        onClose={() => setInfoDialogVisible(false)}
        open={infoDialogVisible}
      >
        {biotwin && <BiotwinInfo biotwin={biotwin} />}
      </Modal>

      <Modal
        className={'w-[30rem]'}
        title={`Sample Settings`}
        onClose={() => setSampleDialogVisible(false)}
        open={sampleDialogVisible}
      >
        <ManageSample userId={userId} />
      </Modal>

      <Modal
        className={'w-[30rem]'}
        title={`Demo Settings`}
        onClose={() => setDemoDialogVisible(false)}
        open={demoDialogVisible}
      >
        <ManageDemo userId={userId} />
      </Modal>
    </>
  );
  async function onRemoveAllConditions() {
    confirm({
      onConfirm: () => {
        resetBiotwin({
          id: userId,
        }).catch(toastError);
      },
      confirmClassName: 'rounded-full',
      title: 'Confirm',
      message: 'Do you really want to remove all conditions?',
      cancelClassName: 'rounded-full bg-transparent',
    });
  }
};
